import MeService from '../me/me.service';
import { useCalendarStore } from './calendar.store';
import { CalendarPersistence } from './calendar.persistence';
import {
  CalendarConnectionStatus,
  type CalendarConnectionStatusResponse,
  type CalendarMetadataDto,
  type InitiateOAuthResponse,
} from './types/calendar.types';
import { ErrorService } from '../errors/error.service';
import { InternalError } from '../errors/internal.error';

export default class CalendarService {
  private meService = new MeService();
  private calendarStore = useCalendarStore();
  private calendarPersistence = new CalendarPersistence();
  private pollingInterval: number | null = null;
  private readonly POLLING_INTERVAL_MS = 2000; // 2 seconds
  private readonly MAX_POLLING_DURATION_MS = 3 * 60 * 1000; // 3 minutes

  async updateCurrentUserConnectionStatus(): Promise<void> {
    const userProfileId = this.meService.userProfile?.id;
    if (!userProfileId) {
      return;
    }
    const statuses = await this.getConnectionStatus([userProfileId]);

    const currentUserStatus = statuses.find((s) => s.userProfileId === userProfileId);
    if (currentUserStatus) {
      this.connectionStatus = currentUserStatus.status;
      this.calendarStore.currentUserNeedsUpgrade = !!currentUserStatus.needsUpgrade;
      this.calendarStore.calendars = currentUserStatus.calendars || [];
      this.calendarStore.defaultCalendarId = currentUserStatus.defaultCalendarId;
    }
  }

  async getConnectionStatus(userProfileIds: number[]): Promise<CalendarConnectionStatusResponse> {
    try {
      return await this.calendarPersistence.getConnectionStatus(userProfileIds);
    } catch (error) {
      ErrorService.captureException(error);
      return [];
    }
  }

  async getInitiateOAuthLink(): Promise<InitiateOAuthResponse> {
    const userProfileId = this.meService.userProfile?.id;
    const redirectUrl = `${window.location.origin}/settings/profile`;
    if (!userProfileId) {
      throw new InternalError('Cannot initiate OAuth connection. User profile ID not found');
    }
    return this.calendarPersistence.getInitiateOAuthLink(redirectUrl);
  }

  /**
   * Changes the UI state to "connecting" and starts polling for connection status.
   * When the connection is stablished (status = active), the user cancels
   * or times out we reset this and stop the polling.
   */
  async connectCalendar(): Promise<void> {
    try {
      this.connectionFlowActive = true;
      this.pollForConnection();
    } catch (error) {
      this.connectionFlowActive = false;
    }
  }

  private pollForConnection(): void {
    const userProfileId = this.meService.userProfile?.id;
    if (!userProfileId) {
      throw new InternalError('Cannot poll for connection. User profile ID not found');
    }
    const startTime = Date.now();

    this.clearPollingInterval();
    this.pollingInterval = window.setInterval(async () => {
      try {
        // Check if max duration exceeded
        if (Date.now() - startTime > this.MAX_POLLING_DURATION_MS) {
          this.stopPolling();
          this.connectionFlowActive = false;
          return;
        }

        await this.updateCurrentUserConnectionStatus();

        if (
          this.connectionStatus === CalendarConnectionStatus.ACTIVE &&
          !this.currentUserNeedsUpgrade
        ) {
          this.stopPolling();
        }
      } catch (error) {
        // Continue polling on error, but log it
        ErrorService.captureException(error);
      }
    }, this.POLLING_INTERVAL_MS);
  }

  clearPollingInterval(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  stopPolling(): void {
    this.clearPollingInterval();
    this.connectionFlowActive = false;
  }

  async disconnectCalendar(): Promise<void> {
    await this.calendarPersistence.disconnectCalendar();
    this.connectionStatus = CalendarConnectionStatus.DISCONNECTED;
  }

  get connectionStatus(): CalendarConnectionStatus | null {
    return this.calendarStore.connectionStatus;
  }

  set connectionStatus(status: CalendarConnectionStatus | null) {
    this.calendarStore.connectionStatus = status;
  }

  get currentUserNeedsUpgrade(): boolean {
    return this.calendarStore.currentUserNeedsUpgrade;
  }

  set currentUserNeedsUpgrade(needsUpgrade: boolean) {
    this.calendarStore.currentUserNeedsUpgrade = needsUpgrade;
  }

  get calendarConnectionOauthUrl(): string | null {
    return this.calendarStore.calendarConnectionOauthUrl;
  }

  set calendarConnectionOauthUrl(url: string | null) {
    this.calendarStore.calendarConnectionOauthUrl = url;
  }

  get connectionFlowActive(): boolean {
    return this.calendarStore.connectionFlowActive;
  }

  set connectionFlowActive(isActive: boolean) {
    this.calendarStore.connectionFlowActive = isActive;
  }

  get calendars(): CalendarMetadataDto[] {
    return this.calendarStore.calendars;
  }

  get writableCalendars(): CalendarMetadataDto[] {
    return this.calendarStore.calendars.filter(
      (calendar) => calendar.enabled && !calendar.readonly,
    );
  }

  get defaultCalendarId(): string | undefined {
    return this.calendarStore.defaultCalendarId;
  }

  set defaultCalendarId(calendarId: string | undefined) {
    this.calendarStore.defaultCalendarId = calendarId;
  }

  async updateDefaultCalendar(calendarId: string): Promise<void> {
    const previousCalendarId = this.defaultCalendarId;
    try {
      await this.calendarPersistence.updateDefaultCalendar({ calendarId });
      this.defaultCalendarId = calendarId;
    } catch (error) {
      this.defaultCalendarId = previousCalendarId;
      throw error;
    }
  }

  async updateCalendarEnabled(calendarId: string, enabled: boolean): Promise<void> {
    const calendar = this.calendarStore.calendars.find((c) => c.id === calendarId);
    if (!calendar) {
      return;
    }

    const previousEnabled = calendar.enabled;
    calendar.enabled = enabled;
    try {
      await this.calendarPersistence.updateCalendarEnabled({ calendarId, enabled });
      // Re-find by id — a connection-status refresh mid-request can replace
      // calendarStore.calendars wholesale, detaching `calendar` from what's rendered.
      const confirmedCalendar = this.calendarStore.calendars.find((c) => c.id === calendarId);
      if (confirmedCalendar) {
        confirmedCalendar.enabled = enabled;
      }
    } catch (error) {
      // Only roll back if the store still holds this exact object — if it's
      // been replaced, that replacement already reflects fresher server
      // data, which our stale previousEnabled must not overwrite.
      if (this.calendarStore.calendars.includes(calendar)) {
        calendar.enabled = previousEnabled;
      }
      throw error;
    }
  }
}
